/*
 * Copyright (c) CIRROM 2025.
 */

import { Theme } from "@mui/system";

export const components = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    /*
     * Disabling the ripple also removed MUI's only keyboard-focus affordance,
     * so the ring is restored here for every ButtonBase descendant - buttons,
     * tabs, icon buttons, menu items. DESIGN.md is explicit that keyboard
     * traversal of dense grids is a real workflow; the ring resolves through
     * the palette so every variant keeps its own action colour.
     */
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        "&.Mui-focusVisible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      contained: {
        textShadow: "0 1px 1px rgba(0, 0, 0, 0.3)",
        boxShadow: "rgba(0, 0, 0, 0.05) 0 2px 4px 0",
      },
      /*
       * The neutral tier: `variant="outlined" color="inherit"`.
       *
       * For actions that neither write nor decide - view, export, add to a list - so the
       * action colour stays reserved for the ones that do. MUI's own inherit-outlined
       * draws its border from currentColor at 50%, far heavier than this system's
       * hairline; this pins it to the same `divider` every other structural line uses.
       *
       * It lives here rather than beside the buttons because a button tier is a
       * system-level definition, and because these values must resolve through the
       * palette to survive dark mode and the six non-CIRROM variants - a stylesheet
       * cannot read them unless the theme is built with `cssVariables`, which it is not.
       */
      outlinedInherit: ({ theme }: { theme: Theme }) => ({
        borderColor: theme.palette.divider,
        color: theme.palette.text.primary,
        "&:hover": {
          borderColor: theme.palette.text.secondary,
          backgroundColor: theme.palette.action.hover,
        },
        "&.Mui-disabled": {
          borderColor: theme.palette.divider,
          color: theme.palette.text.disabled,
        },
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: "hover",
    },
    /* Links sit outside ButtonBase, so the focus ring is declared again. */
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
        },
      }),
    },
  },

  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: {
        variant: "h6",
      },
    },
    styleOverrides: {
      action: {
        marginTop: "-4px",
        marginRight: "-4px",
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: "6px",
        boxShadow:
          "rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px -1px",
        backgroundImage: "none",
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      day: {
        fontWeight: "300",
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      root: {
        height: "64px",
      },
    },
  },
  MuiPickersCalendar: {
    styleOverrides: {
      transitionContainer: {
        marginTop: "6px",
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      iconButton: {
        backgroundColor: "transparent",
        "& > *": {
          backgroundColor: "transparent",
        },
      },
      switchHeader: {
        marginTop: "2px",
        marginBottom: "4px",
      },
    },
  },
  MuiPickersClock: {
    styleOverrides: {
      container: {
        margin: `32px 0 4px`,
      },
    },
  },
  MuiPickersClockNumber: {
    styleOverrides: {
      clockNumber: {
        left: `calc(50% - 16px)`,
        width: "32px",
        height: "32px",
      },
    },
  },
  MuiPickerDTHeader: {
    styleOverrides: {
      dateHeader: {
        "& h4": {
          fontSize: "2.125rem",
          fontWeight: 400,
        },
      },
      timeHeader: {
        "& h3": {
          fontSize: "3rem",
          fontWeight: 400,
        },
      },
    },
  },
  MuiPickersTimePicker: {
    styleOverrides: {
      hourMinuteLabel: {
        "& h2": {
          fontSize: "3.75rem",
          fontWeight: 300,
        },
      },
    },
  },
  MuiPickersToolbar: {
    styleOverrides: {
      toolbar: {
        "& h4": {
          fontSize: "2.125rem",
          fontWeight: 400,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: "6px",
      },
      filled: {
        textShadow: "0 1px 1px rgba(0, 0, 0, 0.2)",
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }: { theme: Theme }) => ({
        border: `1px solid red`,
        borderColor: theme.palette.divider,
      }),
    },
  },
};
